import { useEffect, useRef } from 'react';
import type { Mark } from '@pen/shared';
import { COLORS } from './render';
type XY = readonly [number, number];
function pencil(ctx: CanvasRenderingContext2D, points: readonly XY[], seed: number) {
  ctx.beginPath(); points.forEach(([x, y], i) => { const a = x + Math.sin(i * 2.31 + seed) * .48, b = y + Math.cos(i * 1.73 + seed) * .48; if (i) ctx.lineTo(a, b); else ctx.moveTo(a, b); }); ctx.stroke();
}
function line(a: XY, b: XY): XY[] {
  const count = Math.max(2, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / 7));
  return Array.from({ length: count + 1 }, (_, i) => [a[0] + (b[0] - a[0]) * i / count, a[1] + (b[1] - a[1]) * i / count]);
}
export function drawMark(ctx: CanvasRenderingContext2D, mark: Mark, seed: number) {
  ctx.save(); ctx.strokeStyle = ctx.fillStyle = COLORS[mark.ink]; ctx.lineWidth = 1.7; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  if (mark.kind === 'circle' && mark.box) {
    const [x, y, w, h] = mark.box;
    pencil(ctx, Array.from({ length: 97 }, (_, i): XY => { const a = i / 96 * Math.PI * 2; return [x + w / 2 + Math.cos(a) * Math.max(3, w / 2 + 5), y + h / 2 + Math.sin(a) * Math.max(3, h / 2 + 5)]; }), seed);
  } else if (mark.kind === 'underline' && mark.box) {
    const [x, y, w, h] = mark.box; pencil(ctx, line([x, y + h + 4], [x + w, y + h + 4]), seed);
  } else if (mark.kind === 'arrow' && mark.from && mark.to) {
    pencil(ctx, line(mark.from, mark.to), seed); const angle = Math.atan2(mark.to[1] - mark.from[1], mark.to[0] - mark.from[0]);
    for (const offset of [-.46, .46]) pencil(ctx, line(mark.to, [mark.to[0] - 11 * Math.cos(angle + offset), mark.to[1] - 11 * Math.sin(angle + offset)]), seed + offset);
  } else if (mark.kind === 'note' && mark.at && mark.text) {
    ctx.font = 'italic 18px "Bradley Hand", "Segoe Print", cursive'; ctx.textBaseline = 'top';
    ctx.fillText(mark.text, Math.max(3, Math.min(mark.at[0], 700 - ctx.measureText(mark.text).width - 4)), Math.max(3, mark.at[1]));
  }
  ctx.restore();
}
export function Marks({ turns, height }: { turns: Mark[][]; height: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current, ctx = canvas?.getContext('2d'); if (!canvas || !ctx) return;
    const d = devicePixelRatio || 1; canvas.width = 700 * d; canvas.height = height * d; ctx.setTransform(d, 0, 0, d, 0, 0);
    turns.forEach((marks, i) => { ctx.globalAlpha = i === turns.length - 1 ? 1 : Math.max(.08, .23 - (turns.length - i - 2) * .05); marks.forEach((mark, j) => drawMark(ctx, mark, i * 17 + j)); });
  }, [turns, height]);
  return <canvas ref={ref} className="pw-marks" style={{ width: 700, height }} aria-hidden="true" />;
}
